import React from 'react';
import { Level, Box, useBreakpoint } from '@landr/maestro';
import { useSearchController } from 'controllers/SearchController/SearchContext';
import VerboseError from 'error/components/VerboseError';
import ItemsGrid from 'components/ItemsGrid';
import { PackCard } from 'components/Cards/PackCard';
import { DefaultEmptyState } from 'components/EmptyState/DefaultState/DefaultState';
import Pagination from 'components/Pagination/Pagination';
import { useCurrentSearchState } from 'controllers/SearchController/hooks/useCurrentSearchState';
import { usePrerenderComponentReady } from 'hooks/usePrerenderComponentReady/usePrerenderComponentReady';
import PaneResults from './PaneResults';
import { UpsellBanner } from './UpsellBanner';

const PacksPane = ({
    customEmptyState: CustomEmptyState,
    onPageChanged,
    showNoFilterCopy = false,
    dropdownSorting,
}: {
    customEmptyState?: React.ComponentType;
    onPageChanged: (nextPage: number) => void;
    showNoFilterCopy?: boolean;
    dropdownSorting?: React.ReactNode;
}) => {
    const { packsPerPage, packs, packsError, packsLoading, packsTotal } =
        useSearchController();

    const { hasActiveFilters } = useCurrentSearchState();
    const isMobile = useBreakpoint('md');

    const showEmptyState = !packsLoading && packsTotal === 0;

    usePrerenderComponentReady({
        hasDoneRendering: !!packs,
        componentName: 'PacksPane',
    });

    if (packsError) {
        return <VerboseError />;
    }

    if (showEmptyState) {
        const showCustomEmptyState = !hasActiveFilters && CustomEmptyState;

        return showCustomEmptyState ? (
            <>
                <UpsellBanner />
                <CustomEmptyState />
            </>
        ) : (
            <DefaultEmptyState />
        );
    }

    return (
        <>
            <UpsellBanner />
            <Box mb="md" pt="md" minHeight="30px">
                <Level align="flex-start" mt={isMobile ? '0px' : '3px'}>
                    <Level.Item>
                        <PaneResults
                            isLoading={packsLoading}
                            total={packsTotal}
                            showNoFilterCopy={showNoFilterCopy}
                        />
                    </Level.Item>
                    <Level.Item>{dropdownSorting}</Level.Item>
                </Level>
            </Box>
            <ItemsGrid
                isFetching={packsLoading}
                cardComponent={PackCard}
                items={packs ?? []}
            />
            <Pagination
                itemsPerPage={packsPerPage}
                itemCount={packsTotal ?? 0}
                onPageChanged={onPageChanged}
            />
        </>
    );
};

PacksPane.displayName = 'DetailsView.PacksPane';

export default PacksPane;
